import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AllHtmlEntities } from 'html-entities';
import { SquiggleItem, SavingItem, Completion } from '../types/types';
import './EditItem.css';
import { isNewItemId } from '../biz/itemUtil';
import { useAdventureDataContext } from '../context/AdventureDataContext';
import useCodeMirrorEditor from './useCodeMirrorEditor';
import { ImplicitLinkMatchPosition } from '../biz/linkFinder';

const entities = new AllHtmlEntities();

function EditItem({
  item,
  isSelected,
  stopEditing,
  create,
  link
} : {
  item: SquiggleItem,
  isSelected: boolean,
  stopEditing: (newName: string, newText: string) => void,
  create: (item: SavingItem, parentId?: string) => void,
  link: (itemId: string, selectItem: boolean) => void,
}) {
  const { saveItem, itemLookupTree } = useAdventureDataContext();

  const [initialName, setInitialName] = useState<string>(item.name);
  const [initialText, setInitialText] = useState<string>(item.text);
  const [name, setName] = useState<string>(item.name);
  const [text, setText] = useState<string>(item.text);
  const [linkSpans, setLinkSpans] = useState<ImplicitLinkMatchPosition[]>([]);
  const [suggestions, setSuggestions] = useState<Completion[]>([]);
  const [caretCoordinates, setCaretCoordinates] = useState<{ top: number, left: number } | null>(null); // State for caret position
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const discardDialogRef = useRef<HTMLDialogElement>(null);

  const linkSpansUpdatedCallback = useCallback((linkSpans: ImplicitLinkMatchPosition[]) => {
    setLinkSpans(linkSpans);
  }, []);

  const shadowText = useMemo(() => {
    const encodedText = entities.encode(text);
    let shadow = "";
    let previousIndex = 0;
    for (const span of linkSpans) {
      shadow += encodedText.substring(previousIndex, span.encodedEnd + 1)
        + '<span class="shadowtextlinkicon">\u{1F782}</span>'; // Right-pointing triangle
      previousIndex = span.encodedEnd + 1;
    }
    shadow += encodedText.substring(previousIndex);
    return { __html: shadow.replace(/\n$/, '\n\u200B') };
  }, [text, linkSpans]);

  const editorViewRef = useCodeMirrorEditor({
    containerRef: editorContainerRef,
    placeholder: "Describe the item here using Markdown…",
    text,
    setText,
    linkSpansUpdatedCallback,
    itemLookupTree,
    itemId: item.id,
    readOnly: item.type === 'system',
    link,
    setSuggestions,
    setCaretCoordinates,
  });

  useEffect(() => {
    setInitialName(item.name);
    setInitialText(item.text);
    setName(item.name);
    setText(item.text);
    setSuggestions([]); // Reset suggestions on item change
    setCaretCoordinates(null); // Reset caret position on item change
  }, [item]);

  // Focus on text area and move caret to end on entering editing mode
  useEffect(() => {
    // Apparently this runs after editorViewRef.current is set
    const editor = editorViewRef.current;
    if (editor && editor.state.doc.length > 0) {
      editor.focus();
      editor.dispatch({
        selection: { anchor: editor.state.doc.length },
      });
    }
  }, [editorViewRef /* Here just to satisfy the linter */]);

  // Refocus on text area when item is selected again while already in edit mode
  useEffect(() => {
    const editor = editorViewRef.current;
    const nameInput = nameInputRef.current;
    if (isSelected && editor && nameInput) {
      if (nameInput.value.length === 0) {
        nameInput.focus();
      } else {
        editor.focus();
      }
    }
  }, [isSelected, editorViewRef /* Here just to satisfy the linter */]);

  const isDirty = name !== initialName || text !== initialText;

  const saveCallback = useCallback(() => {
    if (item.type === "system") {
      return Promise.resolve();
    }
    const promise = saveItem({
      id: item.id,
      type: item.type,
      name,
      text
    });

    // Reset initial state after saving
    setInitialName(name);
    setInitialText(text);

    return promise;
  }, [item, name, text, saveItem, setInitialName, setInitialText]);

  const createCallback = useCallback(() => {
    create({
      id: item.id,
      type: item.type,
      name,
      text
    }, item.parent);
  }, [item, name, text, create]);

  const closeDiscardDialog = useCallback(() => {
    discardDialogRef.current?.close();
  }, []);

  const confirmDiscardCallback = useCallback(() => {
    closeDiscardDialog();
    stopEditing(initialName, initialText);
  }, [closeDiscardDialog, stopEditing, initialName, initialText]);

  const stopEditCallback = useCallback(() => {
    if (isDirty) {
      const discardDialog = discardDialogRef.current;
      if (discardDialog?.open) {
        return;
      }
      discardDialog?.showModal();
      return;
    }
    stopEditing(initialName, initialText);
  }, [isDirty, stopEditing, initialName, initialText]);

  useEffect(() => {
    const applySuggestions = () => {
      if (suggestions.length > 0) {
        const editor = editorViewRef.current;
        if (!editor) {
          return;
        }
        const caretPosition = editor.state.selection.main.head;
        const textAfterCaret = text.substring(caretPosition);
        if (!textAfterCaret.toLowerCase().startsWith(suggestions[0].completion.toLowerCase())) {
          const scrollTop = editor.scrollDOM.scrollTop;
          const scrollLeft = editor.scrollDOM.scrollLeft;
          editor.dispatch({
            changes: {
              from: caretPosition,
              to: caretPosition,
              insert: suggestions[0].completion,
            },
            selection: { anchor: caretPosition + suggestions[0].completion.length },
          });
          // Keep the viewport stable when applying inline completions.
          editor.scrollDOM.scrollTop = scrollTop;
          editor.scrollDOM.scrollLeft = scrollLeft;
        }
        setSuggestions([]);
        setCaretCoordinates(null);
      }
    };

    const keyHandlerTextArea = (e: KeyboardEvent) => {
      if (e.key === ' ' && (e.ctrlKey || e.metaKey) && suggestions) {
        applySuggestions();
        e.preventDefault();
      }
      if (e.key.length !== 1 && e.key !== 'Control' && e.key !== 'Shift' && e.key !== 'Meta' && e.key !== 'Alt') {
        if (suggestions.length > 0) {
          setSuggestions([]);
          if (e.key === 'Escape') {
            e.stopPropagation();
          }
        }
      }
    };

    const editor = editorViewRef.current;
    editor?.dom?.addEventListener("keydown", keyHandlerTextArea, true);
    return () => {
      editor?.dom?.removeEventListener("keydown", keyHandlerTextArea, true);
    };
  }, [suggestions, text, editorViewRef /* Here just to satisfy the linter */]);

  useEffect(() => {
    if (!isSelected) {
      return;
    }

    const keyHandlerWindow = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        stopEditCallback();
      }
      if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
        if (e.key === 'S' || e.key === "s") {
          if (isNewItemId(item.id)) {
            createCallback();
          } else {
            saveCallback();
          }
          e.preventDefault();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (isNewItemId(item.id)) {
          createCallback();
        } else {
          saveCallback();
        }
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", keyHandlerWindow, false);
    return () => {
      window.removeEventListener("keydown", keyHandlerWindow, false);
    };
  }, [isSelected, item, stopEditCallback, saveCallback, createCallback]);

  const resetSuggestions = () => {
    setSuggestions([]);
  };

  const suggestionTop = Math.max(8, Math.min((caretCoordinates?.top ?? 80) + 20, window.innerHeight - 40));
  const suggestionLeft = Math.max(8, Math.min(caretCoordinates?.left ?? 24, window.innerWidth - 200));

  return <>
    <div className="formrow editnamerow">
      <input
        autoFocus
        placeholder="Name…"
        id="itemname"
        className={"grow itemname " + (name === initialName ? "" : "dirty")}
        value={name}
        onChange={item.type !== "system" ? (e) => setName(e.target.value) : undefined}
        readOnly={item.type === "system"}
        ref={nameInputRef} />
    </div>
    { item.type === "system" && <div className="formrow sysmsg">System info items cannot be edited.</div>}
    <div className="formrow grow shadowcontainer">
      <div
        className={"editorshell " + (text === initialText ? "" : "dirty")}
        ref={editorContainerRef}
        onClick={resetSuggestions}
        onFocus={resetSuggestions}
        onBlur={resetSuggestions} />
      <div className="shadowtext" dangerouslySetInnerHTML={shadowText}></div>
      {suggestions.length > 0 && (
        <div
          className="suggestions"
          style={{
            top: suggestionTop,
            left: suggestionLeft,
          }}
        >
          {suggestions.map(s => <span key={s.name} className="suggestion">{s.name}</span>)}
        </div>
      )}
    </div>
    <div className="buttonrow reverse">
      {
        isNewItemId(item.id)
        ?
        <div className="buttoncontainer">
          <button onClick={() => { createCallback(); }} className="save">Create</button>
          <button className="cancel" onClick={() => stopEditing(initialName, initialText)}>Cancel</button>
        </div>
        :
        <div className="buttoncontainer">
          <button title="Save & edit [Ctrl+Enter]" onClick={() => { isDirty && saveCallback(); }} className={"save " + (!isDirty ? "disabled" : "")}>Save & edit</button>
          <button title="Save & view" onClick={() => { (isDirty ? saveCallback() : Promise.resolve()).then(() => stopEditing(name, text)); }} className={(!isDirty ? "disabled" : "")}>Save & view</button>
          <button title={isDirty ? "Cancel [Esc]" :"Done [Esc]"} className="cancel" onClick={stopEditCallback}>{isDirty ? "Cancel" : "Done"}</button>
        </div>
      }
    </div>
    <dialog className="discarddialog" ref={discardDialogRef}>
      <p>Discard unsaved changes?</p>
      <div className="buttoncontainer">
        <button type="button" className="cancel" onClick={closeDiscardDialog}>No</button>
        <button type="button" className="discard" onClick={confirmDiscardCallback}>Yes</button>
      </div>
    </dialog>
  </>;
}

export default EditItem;